import CloningConfigOptions from '../cloningConfigOptions'

export const reducerName = 'LEAGUES_CONFIGS'

// Sentinel used when the user has not yet chosen a real season.
// Kept here so the selector and the dropdown share the same reference.
export const SELECT_SEASON_PLACEHOLDER_VALUE = 'select_season_placeholder'

export const BASE_CONFIGS_OPTIONS = [
  { id: CloningConfigOptions.COPY_MANAGERS, capabilityFlag: 'has_managers' },
  { id: CloningConfigOptions.COPY_PLAYER_ROSTER },
  { id: CloningConfigOptions.TOURNAMENT_LIBRARY },
  { id: CloningConfigOptions.GOLF_HUB_PAGE, capabilityFlag: CloningConfigOptions.GOLF_HUB_PAGE },
  { id: CloningConfigOptions.COPY_LOGOS, withInfo: true },
  { id: CloningConfigOptions.COPY_DFT },
  { id: CloningConfigOptions.COPY_ALL_PORTAL_PAGES, withInfo: true },
  { id: CloningConfigOptions.ROUNDS_AND_SERIES },
  { id: CloningConfigOptions.OPEN_REGISTRATION },
  { id: CloningConfigOptions.STRIPE_ACCOUNT, capabilityFlag: 'has_stripe_account' },
  { id: CloningConfigOptions.TOURNAMENTS, dependencies: [ CloningConfigOptions.COPY_DFT, CloningConfigOptions.ROUNDS_AND_SERIES ], withInfo: true },
  { id: CloningConfigOptions.REGISTRATION_PAYMENT_OPTIONS, dependencies: [ CloningConfigOptions.OPEN_REGISTRATION ], capabilityFlag: 'has_registration_payment_options' },
]

// Maps option IDs to the league capability flag that must be present on at least one
// selected league for the option to be shown. Options without an entry are always shown.
export const OPTION_CAPABILITY_FLAGS = BASE_CONFIGS_OPTIONS.reduce((acc, option) => {
  if (option.capabilityFlag) {
    acc[option.id] = option.capabilityFlag
  }
  return acc
}, {})

const CONFIGS_OPTIONS_WITH_DEPENDENCIES =
  BASE_CONFIGS_OPTIONS.filter(configOption => configOption.dependencies)

export const EMPTY_CONFIGS_OPTIONS = {}
export const COMPLETE_CONFIGS_OPTIONS =
  Object.fromEntries(BASE_CONFIGS_OPTIONS.map(option => [ option.id, true ]))

// Reverse map: optionId -> list of option IDs that declare it as a dependency
const REVERSE_DEPENDENCIES = BASE_CONFIGS_OPTIONS.reduce((acc, option) => {
  if (option.dependencies) {
    option.dependencies.forEach(dep => {
      if (!acc[dep]) {
        acc[dep] = []
      }
      acc[dep].push(option.id)
    })
  }
  return acc
}, {})

// Returns true if any currently-selected option lists optionId as a dependency,
// meaning optionId must stay selected and cannot be toggled off.
export const isOptionLockedByDependent = (optionId, configsOptions) => {
  const dependents = REVERSE_DEPENDENCIES[optionId] || []
  return dependents.some(dependentId => configsOptions[dependentId])
}

// When an option with dependencies is selected, auto-select all of its dependencies.
const getSanitizedConfigOptions = (configsOptions) => {
  const result = { ...configsOptions }

  CONFIGS_OPTIONS_WITH_DEPENDENCIES.forEach(option => {
    if (result[option.id]) {
      option.dependencies.forEach(dep => {
        result[dep] = true
      })
    }
  })

  return result
}


const ActionTypes = {
  SET_CONFIGS_OPTIONS: 'SET_CONFIGS_OPTIONS',
  SET_CONFIGS_SEASON: 'SET_CONFIGS_SEASON',
}

const initialState = {
  // The selected config options
  configsOptions: {},
  // Season
  season: {},
}

const LeaguesConfigsReducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_CONFIGS_SEASON:
      return {
        ...state,
        season: action.season,
      }

    case ActionTypes.SET_CONFIGS_OPTIONS:
      return {
        ...state,
        configsOptions: getSanitizedConfigOptions(action.configsOptions),
      }

    default:
      return state
  }
}

const setConfigsOptionsAction = configsOptions => ({
  type: ActionTypes.SET_CONFIGS_OPTIONS,
  configsOptions: configsOptions,
})

export const setConfigsOptions = (configsOptions) => (dispatch) => {
  dispatch(setConfigsOptionsAction(configsOptions))
}

const setConfigsSeasonAction = season => ({
  type: ActionTypes.SET_CONFIGS_SEASON,
  season: season,
})

export const setConfigsSeason = (season) => (dispatch) => {
  dispatch(setConfigsSeasonAction(season))
}

export const selectConfigsOptions = state => state?.[reducerName]?.configsOptions
export const selectConfigsSeason = state => state?.[reducerName]?.season
// Returns true only when the user has picked a real season (not the placeholder, not empty)
export const selectIsSeasonSelected = state => {
  const value = state?.[reducerName]?.season?.value
  return !!value && value !== SELECT_SEASON_PLACEHOLDER_VALUE
}

// Returns the subset of BASE_CONFIGS_OPTIONS relevant to the given leagues.
// Conditional options (with a capabilityFlag) are only included if at least one league
// carries that flag as truthy. Use via the useAvailableConfigOptions hook, not directly.
export const getAvailableConfigOptions = (enhancedLeagues = []) =>
  BASE_CONFIGS_OPTIONS.filter(option => {
    const flag = OPTION_CAPABILITY_FLAGS[option.id]
    if (!flag) {
      return true
    }
    return enhancedLeagues.some(league => league[flag])
  })

export const namedReducer = { [reducerName]: LeaguesConfigsReducer }

export const selectIsRoundsAndSeriesSelected = state => !!state?.[reducerName]?.configsOptions?.[CloningConfigOptions.ROUNDS_AND_SERIES]

export const getInitialState = (cloneLeaguesSession) => {
  const sessionData = cloneLeaguesSession?.data
  const firstLeague = sessionData?.leagues
    ? Object.values(sessionData.leagues)[0]
    : null

  const configsOptions = BASE_CONFIGS_OPTIONS.reduce((acc, option) => {
    // By default, all options except COPY_PLAYER_ROSTER should be selected
    acc[option.id] = firstLeague?.[option.id] ?? (option.id !== CloningConfigOptions.COPY_PLAYER_ROSTER)
    return acc
  }, {})

  return {
    configsOptions: getSanitizedConfigOptions(configsOptions),
    season: firstLeague?.season ? { value: firstLeague.season } : {},
  }
}

export default LeaguesConfigsReducer
